import { useTranslation } from "react-i18next";
import {
  GridColDef,
  getGridStringOperators,
  GridRowParams
} from "@mui/x-data-grid";
import type { GridFilterItem, GridCellParams } from "@mui/x-data-grid-premium";
import { CognitoUserGroups } from "./userUtils";
import { PageWrapper, PageWrapperProps } from "../../common-ui/PageWrapper";
import { useAppSelector } from "../../app/hooks";
import { selectUserCognitoGroups } from "./usersSlice";
import { SysAdminsUsersTable } from "./SysAdminsUsersTable";
import { ProgramAdminsUsersTable } from "./ProgramAdminsUsersTable";
import { EditUser } from "./EditUser";
import { useDispatch } from "react-redux";
import { setModalContent, setShowModal } from "../../common-ui/modalSlice";
import { ProgramMembership } from "../../API";
import { selectPrograms } from "../Programs/programsSlice";
import { useEffect, useState, useMemo, useCallback } from "react";
import { Mixpanel } from "../Analytics/Mixpanel";
import { MixpanelEvents } from "../../app/constants";

const pageDetails: PageWrapperProps = {
  className: "Users",
  headerText: "Users"
};

type UsersProps = {
  disableVirtualization?: boolean;
};

export const Users = ({ disableVirtualization = false }: UsersProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [selectedProgramId, setSelectedProgramId] = useState<string>("");
  const allPrograms = useAppSelector(selectPrograms);
  const currentUsersPermissions = useAppSelector(selectUserCognitoGroups);
  const isSysAdmin = currentUsersPermissions?.includes(
    CognitoUserGroups.SysAdmins
  );
  const isProgramAdmin = currentUsersPermissions?.includes(
    CognitoUserGroups.ProgramAdmins
  );

  const preColumns: GridColDef[] = [
    {
      field: "firstName",
      headerName: t("First Name")
    },
    {
      field: "lastName",
      headerName: t("Last Name")
    },
    {
      field: "email",
      headerName: t("Email")
    },
    {
      field: "role",
      headerName: t("Role"),
      valueGetter: (params) => {
        const programMemberships: ProgramMembership[] =
          params.row.programMemberships.items;
        const activeMemberships = programMemberships?.filter(
          (programMembership) => !programMembership.endDate
        );
        const roleSet = new Set<string>();

        activeMemberships?.forEach((membership) => {
          const role = membership.programRole;
          if (role === "Trainee" || role === "Attending") {
            roleSet.add("Clinician");
          } else {
            roleSet.add(role);
          }
        });

        return Array.from(roleSet).join(", ");
      }
    },
    {
      field: "jobTitle",
      headerName: t("Job Title"),
      valueGetter: (params) => {
        const jobTitle = params.row.jobTitle;
        if (jobTitle) return jobTitle;

        const programMemberships: ProgramMembership[] =
          params.row.programMemberships?.items || [];

        const activeMemberships = programMemberships.filter((m) => !m.endDate);

        // Define priority order
        const priority = ["Trainee", "Attending", "ProgramAdmin"];

        // Find highest priority role
        for (const role of priority) {
          if (activeMemberships.find((m) => m.programRole === role)) {
            return role;
          }
        }

        // If none match priority, return first available role or "N/A"
        return activeMemberships[0]?.programRole || "N/A";
      }
    },
    {
      field: "pgy",
      headerName: "PGY"
    },
    {
      field: "program",
      headerName: t("Program(s)"),
      valueGetter: (params) => {
        const programMemberships: ProgramMembership[] =
          params.row.programMemberships.items;
        const activeMemberships = programMemberships?.filter(
          (programMembership) => !programMembership.endDate
        );
        const programsList = allPrograms.filter((program) =>
          activeMemberships?.some(
            (programMembership) => programMembership.programId === program.id
          )
        );
        const names = programsList?.map((program) => program.name).filter(Boolean) as string[];
        return Array.from(new Set(names)).join(", ");
      }
    },
    {
      field: "trainingProgram",
      headerName: t("Training Program"),
      valueGetter: (params) => {
        const programMemberships: ProgramMembership[] =
          params.row.programMemberships.items;
        const activeMemberships = programMemberships?.filter(
          (programMembership) => !programMembership.endDate
        );
        const programsList = allPrograms.filter((program) =>
          activeMemberships?.some(
            (programMembership) =>
              programMembership.programId === program.id &&
              programMembership.programRole === "Trainee"
          )
        );
        const names = programsList?.map((program) => program.name).filter(Boolean) as string[];
        return Array.from(new Set(names)).join(", ");
      }
    },
    {
      field: "status",
      headerName: t("Status")
    },
    {
      field: "npi",
      headerName: "NPI"
    },
    {
      field: "institutionName",
      headerName: t("Institution(s)"),
      // We currently have no way to sort Users by Institution on the backend.
      sortable: false,
      valueGetter: (params) => {
        const programMemberships: ProgramMembership[] =
          params.row.programMemberships.items;
        const activeMemberships = programMemberships?.filter(
          (programMembership) => !programMembership.endDate
        );
        const programsList = allPrograms.filter((program) =>
          activeMemberships?.some(
            (programMembership) => programMembership.programId === program.id
          )
        );
        const names = programsList
          ?.map((program) => program.institution?.name)
          .filter(Boolean) as string[];
        return Array.from(new Set(names)).join(", ");
      }
    }
  ];

  // Memoize columns to prevent recreating on every render, which could cause remounts
  const columns = useMemo(() => {
    return preColumns.map((column) => {
      const isFilterableField = ["role", "trainingProgram", "jobTitle"].includes(
        column.field
      );

      return {
        ...column,
        flex: column.flex ?? 1,
        filterOperators: isFilterableField
          ? [
              {
                ...(getGridStringOperators().find(
                  (op) => op.value === "contains"
                ) ?? {
                  label: "Contains",
                  value: "contains",
                  getApplyFilterFn: () => null
                }),
                getApplyFilterFn: (filterItem: GridFilterItem) => {
                  if (!filterItem.value) return null;
                  return (params: GridCellParams) => {
                    if (!filterItem.value) return false; // Value is required to apply the filter
                    const filterValue = filterItem.value.toString().toLowerCase();
                    const cellValue = (params.value ?? "")
                      .toString()
                      .toLowerCase();
                    return cellValue.includes(filterValue);
                  };
                }
              }
            ]
          : getGridStringOperators().filter(
              (operator) => operator.value === "contains"
            )
      };
    });
  }, [t, allPrograms]);

  // Memoize handleRowClick to prevent table remounts
  const handleRowClick = useCallback((rowData: GridRowParams<any>) => {
    const { row } = rowData;
    const { id } = row;
    const content = <EditUser id={id} />;
    dispatch(setModalContent(content));
    dispatch(setShowModal(true));
  }, [dispatch]);

  // Memoize table components to prevent remounts
  const sysAdminTable = useMemo(
    () => (
      <SysAdminsUsersTable
        columns={columns}
        disableVirtualization={disableVirtualization}
        handleRowClick={handleRowClick}
        selectedProgramId={selectedProgramId}
        setSelectedProgramId={setSelectedProgramId}
      />
    ),
    [columns, disableVirtualization, handleRowClick, selectedProgramId, setSelectedProgramId]
  );
  
  const programAdminTable = useMemo(
    () => (
      <ProgramAdminsUsersTable
        columns={columns}
        disableVirtualization={disableVirtualization}
        handleRowClick={handleRowClick}
      />
    ),
    [columns, disableVirtualization, handleRowClick]
  );

  const tableToDisplay = useMemo(() => {
    return isSysAdmin
      ? sysAdminTable
      : isProgramAdmin
      ? programAdminTable
      : null;
  }, [isSysAdmin, isProgramAdmin, sysAdminTable, programAdminTable]);

  useEffect(() => {
    Mixpanel.track(MixpanelEvents.HomePageViewed);
  }, []);

  return <PageWrapper {...pageDetails}>{tableToDisplay}</PageWrapper>;
};
